import { useLocation } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { SECTORS } from '../lib/sectors';
import { FAQ_ITEMS } from '../lib/adelContent';
import { GULF_GUIDES, GUIDES_PUBLISHED } from '../lib/gulfGuides';
import industriesData from '../data/industriesData.json';

const BASE_TITLE = 'Adel Group';
const BASE_URL = 'https://adelgroups.com';

/* Gulf countries Adel serves — reused for Occupation.occupationLocation. */
const GULF = ['Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Oman', 'Bahrain', 'Kuwait'];

/* Per-route titles + descriptions. Covers the live v2 IA (/candidates/*, /employers/*,
   /sectors/:slug) plus retained legacy routes. Fee rule: only ever "No registration fee".
   These are rendered into <head> declaratively (via <Head>) so vite-react-ssg captures
   real per-URL <title>/<meta>/<link>/JSON-LD in the pre-rendered HTML — visible to AI
   crawlers and link-preview bots that never run JS. */
const ROUTE_SEO = {
  '/': {
    title: 'Adel Group – Recruitment & Manpower Solutions | Kenya to Middle East',
    description: 'Adel Group – Kenya-based recruitment agency for the Middle East. Skilled manpower, documentation & deployment. Submit your CV or request talent. Nairobi.',
  },

  '/trust': {
    title: 'Licensed & Verified — NEA & ASMAK | Adel Group',
    description: 'Adel Group is a licensed Kenyan recruitment agency (NEA licence RA/2025/09/56, ASMAK member). Learn how to verify us and avoid recruitment scams.',
    crumb: 'Trust & licensing',
  },
  '/faq': {
    title: 'FAQ — Fees, Licensing, Gulf Jobs & Hiring | Adel Group',
    description: 'Answers for Kenyan jobseekers and Gulf employers: does Adel charge a fee (no registration fee), are we licensed (NEA RA/2025/09/56, ASMAK), which countries and sectors we cover, and how long placement takes.',
    crumb: 'FAQ',
  },

  /* ---- Candidate journey ---- */
  '/candidates': {
    title: 'Gulf Jobs for Kenyans — Work Abroad | Adel Group',
    description: 'Adel Group places Kenyan workers in real Gulf jobs — no registration fee. We prepare you, handle your documentation and travel, and stay with you after you land.',
    crumb: 'For jobseekers',
  },
  '/candidates/journey': {
    title: 'Your Journey Abroad, Step by Step | Adel Group',
    description: 'How Adel Group guides Kenyan jobseekers from first contact to life in the Gulf: matching, interview prep, documentation, travel and support after you arrive.',
    crumb: 'Your journey', parent: { name: 'For jobseekers', path: '/candidates' },
  },
  '/candidates/sectors': {
    title: 'Gulf Jobs by Sector for Kenyans | Adel Group',
    description: 'Construction, hospitality, healthcare, logistics, security and more — real Gulf roles across 17 sectors for Kenyan workers. Find where you fit, then apply.',
    crumb: 'Sectors', parent: { name: 'For jobseekers', path: '/candidates' },
  },
  '/candidates/jobs': {
    title: 'Current Gulf Roles for Kenyan Workers | Adel Group',
    description: 'See the kinds of roles Adel Group places across the Gulf — skilled and semi-skilled jobs for Kenyan workers. No registration fee to begin.',
    crumb: 'Open roles', parent: { name: 'For jobseekers', path: '/candidates' },
  },
  '/candidates/apply': {
    title: 'Submit Your CV — No Registration Fee | Adel Group',
    description: 'Send your details or apply on WhatsApp — a real person reads every application. Adel Group places Kenyan workers in the Gulf, with no registration fee.',
    crumb: 'Submit your CV', parent: { name: 'For jobseekers', path: '/candidates' },
  },

  /* ---- Employer journey ---- */
  '/employers': {
    title: 'Hire Skilled Workers from Kenya | Adel Group',
    description: 'Adel Group sources quality candidates from Kenya for Gulf employers — you interview and choose; we handle documentation, visas and travel. Mobilise in as little as 14 days.',
    crumb: 'For employers',
  },
  '/employers/how-it-works': {
    title: 'How It Works — From Brief to Arrival | Adel Group',
    description: 'Share your requirement, review quality candidates, interview and choose — Adel Group handles documentation and travel end to end. Simple, from brief to arrival.',
    crumb: 'How it works', parent: { name: 'For employers', path: '/employers' },
  },
  '/employers/why-adel': {
    title: 'Why Employers Choose Adel Group | Kenya Recruitment',
    description: 'Quality candidates matched to your roles, papers in order, and one accountable team from brief to arrival. See why Gulf employers partner with Adel Group.',
    crumb: 'Why Adel', parent: { name: 'For employers', path: '/employers' },
  },
  '/employers/sectors': {
    title: 'Sectors We Staff for Gulf Employers | Adel Group',
    description: 'Construction, hospitality, healthcare, logistics, security and more — Adel Group staffs 17 sectors for Gulf employers with skilled workers from Kenya.',
    crumb: 'Sectors', parent: { name: 'For employers', path: '/employers' },
  },
  '/employers/brief': {
    title: 'Request Workers — Send Your Brief | Adel Group',
    description: 'Tell us the roles and headcount you need. Adel Group replies with salary bands and lead times, then sources quality candidates for you to review and choose.',
    crumb: 'Request workers', parent: { name: 'For employers', path: '/employers' },
  },

  /* ---- Legacy routes (retained until 301s land at hosting time) ---- */
  '/about': {
    title: 'About Us | Adel Group – Recruitment Agency Kenya',
    description: 'Based in Nairobi, Adel Group connects Kenyan talent with employers in the Middle East. Transparent, compliant recruitment and manpower solutions.',
  },
  '/services': {
    title: 'Our Services | Manpower & Recruitment – Adel Group',
    description: 'Manpower recruitment, project staffing, documentation and post-deployment support. End-to-end recruitment from Kenya to the Middle East.',
  },
  '/industries': {
    title: 'Industries We Serve | Adel Group Recruitment',
    description: 'Construction, facilities, manufacturing, hospitality, logistics, security and more. Role-based recruitment across industries for the Middle East.',
  },
  '/jobs': {
    title: 'Open Roles | Middle East Jobs for Kenyan Talent – Adel Group',
    description: 'Active job openings across the Gulf for Kenyan candidates. Construction, hospitality, healthcare, logistics, manufacturing and more.',
  },
  '/job-seekers': {
    title: 'Submit Your CV | Job Opportunities Middle East – Adel Group',
    description: 'Submit your CV for opportunities in the Middle East. Skilled and semi-skilled roles. Quick matching. Adel Group – recruitment from Kenya.',
  },
  '/contact': {
    title: 'Contact Us | Adel Group – Nairobi Recruitment',
    description: 'Get in touch for manpower needs or recruitment enquiries. Nairobi, Kenya. Phone, email. Adel Group – recruitment to the Middle East.',
  },
};

function breadcrumbs(items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: `${BASE_URL}${it.path}`,
    })),
  };
}

/* "Roles we place" as Occupation — the legitimate, non-vacancy alternative to JobPosting.
   No datePosted/validThrough (those belong ONLY on real dated vacancies). */
function occupationList(name, roles) {
  if (!roles || !roles.length) return null;
  return {
    '@type': 'ItemList',
    name: `${name} — roles we place`,
    itemListElement: roles.map((title, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Occupation',
        name: title,
        occupationLocation: GULF.map((c) => ({ '@type': 'Country', name: c })),
      },
    })),
  };
}

function buildIndustriesIndexGraph() {
  return [
    breadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Industries', path: '/industries' },
    ]),
    {
      '@type': 'CollectionPage',
      name: 'Industries We Serve',
      url: `${BASE_URL}/industries`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: industriesData.map((ind, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: ind.name,
          url: `${BASE_URL}/industries/${ind.slug}`,
        })),
      },
    },
  ];
}

function buildFaqGraph() {
  return {
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

/* Resolve pathname → { title, description, noindex?, graph? }. */
function resolveSEO(pathname) {
  /* Gulf country guides — gated: noindex + no schema until GUIDES_PUBLISHED (figures verified). */
  if (pathname === '/candidates/guides') {
    return {
      title: 'Gulf Jobs Guide for Kenyans — Pay, Visas & Worker Rights | Adel Group',
      description: 'Real, dated guides to working in Saudi Arabia, the UAE, Qatar, Oman, Bahrain and Kuwait — what the Gulf pays, who covers your visa and travel, and the rights that protect Kenyan workers.',
      noindex: !GUIDES_PUBLISHED,
      graph: [
        breadcrumbs([
          { name: 'Home', path: '/' },
          { name: 'For jobseekers', path: '/candidates' },
          { name: 'Working in the Gulf', path: '/candidates/guides' },
        ]),
      ],
    };
  }
  const guideM = pathname.match(/^\/candidates\/guides\/([^/?#]+)/);
  if (guideM) {
    const g = GULF_GUIDES.find((x) => x.slug === guideM[1]);
    if (!g) return { title: 'Page not found | Adel Group', description: '', noindex: true };
    return {
      title: `Working in ${g.country} — Gulf Jobs, Pay & Rights for Kenyans | Adel Group`,
      description: (g.intro || '').slice(0, 158),
      noindex: !GUIDES_PUBLISHED,
      graph: [
        breadcrumbs([
          { name: 'Home', path: '/' },
          { name: 'For jobseekers', path: '/candidates' },
          { name: 'Working in the Gulf', path: '/candidates/guides' },
          { name: g.country, path: `/candidates/guides/${g.slug}` },
        ]),
        { '@type': 'WebPage', name: `Working in ${g.country}`, url: `${BASE_URL}/candidates/guides/${g.slug}`, description: g.intro, isPartOf: { '@id': `${BASE_URL}/#website` } },
      ],
    };
  }

  /* /sectors/:slug — the live v2 sector detail page */
  const sectorM = pathname.match(/^\/sectors\/([^/?#]+)/);
  if (sectorM) {
    const s = SECTORS.find((x) => x.slug === sectorM[1]);
    if (!s) return { title: 'Page not found | Adel Group', description: '', noindex: true };
    return {
      title: `${s.name} — Gulf Jobs & Staffing from Kenya | Adel Group`,
      description: (s.blurb || s.intro || '').slice(0, 158),
      graph: [
        breadcrumbs([
          { name: 'Home', path: '/' },
          { name: 'Sectors', path: '/employers/sectors' },
          { name: s.name, path: `/sectors/${s.slug}` },
        ]),
        { '@type': 'WebPage', name: s.name, url: `${BASE_URL}/sectors/${s.slug}`, description: s.intro, isPartOf: { '@id': `${BASE_URL}/#website` } },
        occupationList(s.name, s.roles),
      ].filter(Boolean),
    };
  }

  /* /industries/:slug — legacy detail (kept until 301s land; JobPosting removed) */
  const indM = pathname.match(/^\/industries\/([^/?#]+)/);
  if (indM) {
    const ind = industriesData.find((i) => i.slug === indM[1]);
    if (!ind) return { title: 'Page not found | Adel Group', description: '', noindex: true };
    return {
      title: `${ind.name} | Industries We Serve – Adel Group`,
      description: (ind.intro || '').slice(0, 158) || ROUTE_SEO['/industries'].description,
      graph: [
        breadcrumbs([
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
          { name: ind.name, path: `/industries/${ind.slug}` },
        ]),
        { '@type': 'WebPage', name: ind.name, url: `${BASE_URL}/industries/${ind.slug}`, description: ind.intro, isPartOf: { '@id': `${BASE_URL}/#website` } },
        occupationList(ind.name, ind.jobs),
      ].filter(Boolean),
    };
  }

  const st = ROUTE_SEO[pathname];
  if (st) {
    let graph = null;
    if (pathname === '/industries') {
      graph = buildIndustriesIndexGraph();
    } else if (pathname === '/faq') {
      graph = [
        breadcrumbs([{ name: 'Home', path: '/' }, { name: 'FAQ', path: '/faq' }]),
        buildFaqGraph(),
      ];
    } else if (st.crumb) {
      const crumbs = [{ name: 'Home', path: '/' }];
      if (st.parent) crumbs.push(st.parent);
      crumbs.push({ name: st.crumb, path: pathname });
      graph = [breadcrumbs(crumbs)];
    }
    return { title: st.title, description: st.description, graph };
  }

  /* Unknown route (incl. /admin) → 404-style: noindex, no schema. */
  return { title: 'Page not found | Adel Group', description: '', noindex: true };
}

export default function PageSEO() {
  const { pathname } = useLocation();
  const { title, description, noindex, graph } = resolveSEO(pathname);
  /* Home canonicalises to the trailing-slash root (matches sitemap.xml + index.html). */
  const canonical = `${BASE_URL}${pathname === '/' ? '/' : pathname}`;
  const pageTitle = title || BASE_TITLE;
  const desc = description || '';
  const jsonLd =
    !noindex && graph && graph.length
      ? JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
      : null;

  /* Rendered declaratively via react-helmet-async (vite-react-ssg's <Head>) so the tags
     land in the document <head> of the pre-rendered HTML — not mutated in post-mount JS.
     og:type/site_name/locale, twitter:card and the global Organization/EmploymentAgency/
     WebSite JSON-LD stay static in index.html (constant across routes). */
  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
      {jsonLd ? (
        <script type="application/ld+json">{jsonLd}</script>
      ) : null}
    </Head>
  );
}
